import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { IndividualChat } from "../../../types/chat.type";
import { Flex, Text, View } from "@adobe/react-spectrum";
import { getInitialsAndColor } from "../../../utils";
import { formatLabel } from "../../../utils/utils";
import { useCustomerAttributes } from "../../../hooks/useCustomerAttributes";
import "./style.css";
import { BusinessAttributes } from "../../../types/agent.type";

const UserInfo = ({ activeChatId }: { activeChatId: string }) => {
  const agent = useSelector((state: RootState) => state.agent);
  const chat = useSelector((state: RootState) => state.chat);
  const history = useSelector((state: RootState) => state.history);
  const tabName = useSelector((state: RootState) => state.common.tab);
  const [activeChat, setActiveChat] = useState<IndividualChat | null>(null);

  useEffect(() => {
    const findActiveChat = (): IndividualChat | null => {
      if (tabName === "History") {
        return (
          history.chat?.find(
            (chatItem: IndividualChat) => chatItem.id === activeChatId
          ) || null
        );
      }
      return (
        chat.chats.find(
          (chatItem: IndividualChat) => chatItem.id === chat.activeChatID
        ) || null
      );
    };

    setActiveChat(findActiveChat());
  }, [tabName, activeChatId, history.chat, chat.activeChatID, chat.chats]);

  const customerAttrs = activeChat?.customer?.attributes ?? {};
  const businessAttrs = agent?.businessAttributes ?? [];

  const { groupedAttributes } = useCustomerAttributes({
    customerAttrs,
    businessAttrs,
    type: "detail",
  });

  const initials = useMemo(() => {
    const first = customerAttrs.FIRSTNAME?.[0] || "";
    const last = customerAttrs.LASTNAME?.[0] || "";
    return first && last ? `${first}${last}` : "NA";
  }, [customerAttrs]);

  const avatarColor = useMemo(
    () => getInitialsAndColor(initials, "color"),
    [initials]
  );

  const renderAttributeGroup = (group: BusinessAttributes[], index: number) => (
    <View key={`group-${index}`}>
      <Flex direction="row" wrap="wrap">
        {group.map((attr, idx) => (
          <Flex
            key={`attr-${index}-${idx}`}
            direction="column"
            UNSAFE_style={{ width: "95%" }}
          >
            <Text UNSAFE_className="chat-userinfo-attribute-name">
              {attr.attributeName.toLowerCase() === "notes"
                ? "Transfer Notes"
                : formatLabel(attr.attributeName)}
            </Text>
            <Text UNSAFE_className="chat-userinfo-attribute-description">
              {customerAttrs[attr.attributeName] || "-"}
            </Text>
          </Flex>
        ))}
      </Flex>
    </View>
  );

  return (
    <View UNSAFE_className="chat-userinfo-container">
      <View
        UNSAFE_className="call-history-avatar"
        UNSAFE_style={{ marginTop: "6%" }}
      >
        <Flex UNSAFE_style={{ gap: "4.54%" }}>
          <Flex
            justifyContent="center"
            alignItems="center"
            UNSAFE_className="agent-avatar"
            UNSAFE_style={{
              backgroundColor: avatarColor as string,
              borderRadius: "50%",
            }}
            marginTop="7px"
          >
            <Text UNSAFE_className="call-monitor-agent-text">{initials}</Text>
          </Flex>
          <Flex direction="column" UNSAFE_style={{ marginBottom: "7.2%" }}>
            <Text UNSAFE_className="user-name">
              {customerAttrs.FULLNAME || "-"}
            </Text>
            <Text UNSAFE_className="user-email">
              {customerAttrs.EMAIL || "-"}
            </Text>
          </Flex>
        </Flex>
      </View>

      <View UNSAFE_className="chat-history-attribute-container">
        {groupedAttributes.map(renderAttributeGroup)}
      </View>
    </View>
  );
};

export default UserInfo;
